/**
 * App Bootstrap Hooks
 * 
 * These hooks load core identity and layout data needed for the entire app.
 * They are designed to be called at the Layout level to provide fast initial load.
 * 
 * Query Strategy:
 * - useAppBootstrap: Core identity (user, company, permissions, features, plan)
 *   - staleTime: 60s (1 minute) - user/plan/permissions might change but not every second
 *   - gcTime: 5 min - keep in memory for quick navigation
 *   - refetchOnMount: always - important for login/auto-login transitions
 *   - refetchOnWindowFocus: false - don't spam backend on tab switch
 * 
 * - useHeaderCounts: Dynamic header counters (notifications, tasks, leads)
 *   - staleTime: 10s - considered fresh for 10 seconds
 *   - gcTime: 60s - keep in memory for 1 minute
 *   - refetchInterval: 15s - poll every 15 seconds in background
 *   - refetchOnWindowFocus: true - update when user returns
 */

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { apiClient } from '../../api/apiClient';
import { queryOptions } from '../queryClient';
import { getCompanySlugFromUrl } from '../../utils/company';

/**
 * Core identity data needed for app bootstrap
 */
export interface AppBootstrapData {
  user: {
    _id: string;
    fullName?: string;
    email?: string;
    phone?: string;
    role?: 'client' | 'admin' | 'manager' | 'agent' | 'superadmin';
    permissions?: string[];
    avatar?: string;
  };
  company: {
    _id?: string;
    name: string;
    slug: string;
    logoUrl?: string | null;
  };
  plan: {
    name: string | null;
    features: string[] | null;
    limits?: {
      clients?: number;
      managers?: number;
      tasks?: number;
      leads?: number;
      [key: string]: any;
    };
    usage?: {
      clients?: number;
      managers?: number;
      tasks?: number;
      leads?: number;
      [key: string]: any;
    };
  };
  permissions: {
    matrix: Record<string, boolean>;
    roles: string[];
  };
  features: {
    enabled: string[] | null;
    hiddenFromSidebar: string[];
    locked: string[];
  };
  layout?: {
    sidebarItems?: string[];
    visibleModules?: string[];
  };
}

/**
 * Fetch app bootstrap data (core identity + layout config)
 * 
 * This hook loads all the "core identity" and rarely-changing data needed for:
 * - Layout rendering (sidebar items, permissions)
 * - User info (name, role, avatar)
 * - Company info (name, logo, slug)
 * - Plan info (name, features, limits, usage)
 * - Permissions matrix
 * - Feature flags
 * 
 * @param companySlug - Company slug (required)
 * @param options - Additional React Query options
 * @returns Query result with bootstrap data
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = useAppBootstrap(companySlug);
 * if (isLoading) return <Skeleton />;
 * const userName = data?.user?.fullName || 'User';
 * ```
 */
export function useAppBootstrap(
  companySlug: string | undefined,
  options?: Omit<UseQueryOptions<AppBootstrapData>, 'queryKey' | 'queryFn'>
) {
  // CRITICAL: Always validate and normalize companySlug from URL
  // If companySlug is not provided or invalid, get it from URL
  const validatedSlug = companySlug || getCompanySlugFromUrl();
  
  // Safety check: if slug is invalid, reset to "test" and warn
  const finalSlug = validatedSlug || 'test';
  
  if (companySlug && companySlug !== finalSlug) {
    console.warn('[useAppBootstrap] Invalid company slug detected, resetting to valid slug:', {
      invalid: companySlug,
      valid: finalSlug,
      pathname: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    });
  }
  
  // Determine account type: superadmin if slug is 'superadmin', otherwise tenant
  const accountType: 'superadmin' | 'tenant' = finalSlug === 'superadmin' ? 'superadmin' : 'tenant';
  
  return useQuery<AppBootstrapData>({
    queryKey: ['appBootstrap', finalSlug, accountType],
    queryFn: async () => {
      if (!finalSlug) {
        throw new Error('Company slug is required');
      }

      try {
        // CRITICAL: Always build route with validated slug
        // Route format: /api/${companySlug}/rpc/settings/bootstrap/app
        const bootstrapRoute = `/api/${finalSlug}/rpc/settings/bootstrap/app`;
        
        // Try to use a single bootstrap endpoint if it exists
        const res = await apiClient.post<{ ok: boolean; data: AppBootstrapData }>(
          bootstrapRoute,
          {},
          { 
            withCredentials: true,
            // Accept 404 as valid response (endpoint might not exist yet) - prevents axios from throwing
            validateStatus: (status) => status >= 200 && status < 500
          }
        );

        // Handle 404 gracefully - endpoint doesn't exist, use fallback
        if (res.status === 404) {
          // Log a single warning for 404 (not error spam)
          if (process.env.NODE_ENV === 'development') {
            console.warn('[useAppBootstrap] Bootstrap endpoint not found (404), falling back to aggregation');
          }
          throw new Error('Bootstrap endpoint not found, falling back to aggregation');
        }

        const data = res.data;
        if (data?.ok && data?.data) {
          return data.data;
        }

        // If response doesn't have expected structure, use fallback
        throw new Error('Bootstrap endpoint not found, falling back to aggregation');
      } catch (err: any) {
        // If endpoint doesn't exist (404), aggregate from multiple endpoints
        // Silently handle 404 - this is expected for companies without the new endpoint
        if (err?.response?.status === 404 || err?.message?.includes('Bootstrap endpoint not found')) {
          // Only log in development, and only as debug (not warning)
          if (process.env.NODE_ENV === 'development') {
            console.debug('[useAppBootstrap] Using aggregated endpoints (bootstrap/app not available)');
          }
          
          // Parallel fetch from multiple endpoints
          // All endpoints are wrapped in catch handlers to silently handle any errors (404, 500, etc.)
          // CRITICAL: Always use validated finalSlug for all routes
          const [headerBootstrap, featuresData] = await Promise.allSettled([
            // Try header bootstrap endpoint (already exists)
            apiClient.post<{ ok: boolean; data: any }>(
              `/api/${finalSlug}/rpc/settings/bootstrap/header`,
              {},
              { 
                withCredentials: true,
                // Accept all status codes to prevent axios from throwing
                validateStatus: () => true
              }
            ).then(res => {
              // Only return data if status is 200 and response is ok
              if (res.status === 200 && res.data?.ok) {
                return res.data.data;
              }
              return null;
            }).catch(() => null), // Silently handle any errors
            
            // Try company features endpoint
            apiClient.post<{ ok: boolean; data: any }>(
              `/api/${finalSlug}/rpc/settings/get-company-features`,
              {},
              { 
                withCredentials: true,
                // Accept all status codes to prevent axios from throwing
                validateStatus: () => true
              }
            ).then(res => {
              // Only return data if status is 200 and response is ok
              if (res.status === 200 && res.data?.ok) {
                return res.data.data;
              }
              return null;
            }).catch(() => null), // Silently handle any errors
          ]);

          const header = headerBootstrap.status === 'fulfilled' ? headerBootstrap.value : null;
          const features = featuresData.status === 'fulfilled' ? featuresData.value : null;

          // Build aggregated response
          return {
            user: header?.user || { _id: '', fullName: '', email: '', role: 'client' as const },
            company: {
              name: header?.company?.name || finalSlug,
              slug: finalSlug,
              logoUrl: header?.company?.logoUrl || null,
            },
            plan: header?.plan || { name: null, features: null },
            permissions: {
              matrix: {},
              roles: header?.user?.permissions || [],
            },
            features: {
              enabled: features?.enabledFeatures ?? header?.features?.enabled ?? null,
              hiddenFromSidebar: features?.hiddenFromSidebar ?? header?.features?.hiddenFromSidebar ?? [],
              locked: features?.locked ?? header?.features?.locked ?? [],
            },
            layout: {
              sidebarItems: [],
              visibleModules: [],
            },
          };
        }

        // For other errors (500, network errors, etc.), return safe defaults instead of throwing
        // This prevents the app from crashing if bootstrap fails and avoids console errors
        if (process.env.NODE_ENV === 'development') {
          console.warn('[useAppBootstrap] Bootstrap error, using safe defaults:', err?.message || err);
        }
        
        // Return safe defaults instead of throwing - app should still work even if bootstrap fails
        return {
          user: { _id: '', fullName: '', email: '', role: 'client' as const },
          company: {
            name: finalSlug,
            slug: finalSlug,
            logoUrl: null,
          },
          plan: { name: null, features: null },
          permissions: {
            matrix: {},
            roles: [],
          },
          features: {
            enabled: null,
            hiddenFromSidebar: [],
            locked: [],
          },
          layout: {
            sidebarItems: [],
            visibleModules: [],
          },
        };
      }
    },
    enabled: !!finalSlug,
    staleTime: 60_000, // 1 minute - user/plan/permissions might change but not every second
    gcTime: 5 * 60_000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: true, // Always refetch on mount (important for login/auto-login)
    // Don't retry on 404 errors (company doesn't exist or endpoint not available)
    retry: (failureCount, error: any) => {
      const status = error?.response?.status ?? error?.status;
      // Don't retry on 404 - company doesn't exist or endpoint not available
      if (status === 404) return false;
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    // Note: Matches architecture spec exactly (not using preset to allow override)
    ...options,
  });
}

/**
 * Header counts data (notifications, tasks, leads, etc.)
 */
export interface HeaderCountsData {
  notifications: number;
  tasks: number;
  leads: number;
  messages?: number;
  bugs?: number;
  [key: string]: number | undefined;
}

/**
 * Fetch header counts (dynamic counters for badges)
 * 
 * This hook fetches lightweight count queries for header badges:
 * - Unread notifications count
 * - Open tasks count
 * - Open leads count
 * - Other dynamic counters
 * 
 * The header should NOT wait for these - show 0 or placeholder while loading.
 * 
 * @param companySlug - Company slug (required)
 * @param options - Additional React Query options
 * @returns Query result with counts
 * 
 * @example
 * ```tsx
 * const { data } = useHeaderCounts(companySlug);
 * const taskCount = data?.tasks || 0;
 * ```
 */
export function useHeaderCounts(
  companySlug: string | undefined,
  userId?: string | undefined,
  options?: Omit<UseQueryOptions<HeaderCountsData>, 'queryKey' | 'queryFn'>
) {
  // CRITICAL: Always validate and normalize companySlug from URL
  // If companySlug is not provided or invalid, get it from URL
  const validatedSlug = companySlug || getCompanySlugFromUrl();
  
  // Safety check: if slug is invalid, reset to "test" and warn
  const finalSlug = validatedSlug || 'test';
  
  if (companySlug && companySlug !== finalSlug) {
    console.warn('[useHeaderCounts] Invalid company slug detected, resetting to valid slug:', {
      invalid: companySlug,
      valid: finalSlug,
      pathname: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    });
  }
  
  // Determine account type: superadmin if slug is 'superadmin', otherwise tenant
  const accountType: 'superadmin' | 'tenant' = finalSlug === 'superadmin' ? 'superadmin' : 'tenant';
  
  return useQuery<HeaderCountsData>({
    queryKey: ['headerCounts', finalSlug, accountType, userId || ''],
    queryFn: async () => {
      if (!finalSlug) {
        throw new Error('Company slug is required');
      }

      // CRITICAL: Superadmin doesn't have company-based notifications/tasks/leads
      // Skip API calls for superadmin routes to prevent 404/connection errors
      if (accountType === 'superadmin') {
        return {
          notifications: 0,
          tasks: 0,
          leads: 0,
        };
      }

      // IMPORTANT: Only make API calls when user is authenticated
      // This prevents 500 errors during login/bootstrap phases
      if (!userId) {
        return {
          notifications: 0,
          tasks: 0,
          leads: 0,
        };
      }

      try {
        // CRITICAL: Always build route with validated slug
        // Route format: /api/${companySlug}/rpc/header/counts
        const countsRoute = `/api/${finalSlug}/rpc/header/counts`;
        
        // Try to use a single aggregated endpoint if it exists
        const { data } = await apiClient.post<{ ok: boolean; data: HeaderCountsData }>(
          countsRoute,
          {},
          { withCredentials: true }
        );

        if (data?.ok && data?.data) {
          return data.data;
        }

        // If single endpoint doesn't exist, aggregate from multiple endpoints
        throw new Error('Header counts endpoint not found, falling back to aggregation');
      } catch (err: any) {
        // If endpoint doesn't exist (404), aggregate from multiple endpoints
        if (err?.response?.status === 404 || err?.message?.includes('Header counts endpoint not found')) {
          console.warn('[useHeaderCounts] Using aggregated endpoints');
          
          // Parallel fetch from multiple endpoints
          // CRITICAL: Always use validated finalSlug for all routes
          // IMPORTANT: Only call APIs when user is authenticated
          const [notificationsCount, tasksCount, leadsCount] = await Promise.allSettled([
            // Notifications count - only call if user is authenticated
            userId ? apiClient.post<{ ok: boolean; data: { count: number } }>(
              `/api/${finalSlug}/rpc/notifications/unread-count`,
              {},
              { withCredentials: true }
            ).then(res => res.data?.ok ? res.data.data?.count ?? 0 : 0).catch(() => 0) : Promise.resolve(0),
            
            // Tasks count (open tasks)
            userId ? apiClient.post<{ ok: boolean; data: { count: number } }>(
              `/api/${finalSlug}/rpc/tasks/open-count`,
              {},
              { withCredentials: true }
            ).then(res => res.data?.ok ? res.data.data?.count ?? 0 : 0).catch(() => 0) : Promise.resolve(0),
            
            // Leads count (open leads)
            userId ? apiClient.post<{ ok: boolean; data: { count: number } }>(
              `/api/${finalSlug}/rpc/leads/open-count`,
              {},
              { withCredentials: true }
            ).then(res => res.data?.ok ? res.data.data?.count ?? 0 : 0).catch(() => 0) : Promise.resolve(0),
          ]);

          return {
            notifications: notificationsCount.status === 'fulfilled' ? notificationsCount.value : 0,
            tasks: tasksCount.status === 'fulfilled' ? tasksCount.value : 0,
            leads: leadsCount.status === 'fulfilled' ? leadsCount.value : 0,
          };
        }

        // For other errors, return safe defaults
        console.warn('[useHeaderCounts] Error fetching counts:', err?.message || err);
        return {
          notifications: 0,
          tasks: 0,
          leads: 0,
        };
      }
    },
    // IMPORTANT: Only enable query when company AND user are ready
    // This prevents API calls before authentication is complete
    enabled: !!finalSlug && !!userId,
    ...queryOptions.realtimeWidget, // realtimeWidget: 10s stale, 60s cache, 15s interval, refetch on focus
    // Note: Matches architecture spec exactly
    ...options,
  });
}

